import { Component, computed, input, model, output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  Item,
  SelectFieldComponent,
} from '../../form/select-field/select-field.component';

/**
 * A calendar view option.
 */
export interface CalendarToolbarViewOption {
  value: string;
  label: string;
  icon?: string;
  viewType?: string;
}

@Component({
  selector: 'siat-calendar-toolbar',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatMenuModule,
    SelectFieldComponent,
  ],
  templateUrl: './calendar-toolbar.component.html',
  styleUrl: './calendar-toolbar.component.scss',
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    provideNativeDateAdapter(),
  ],
})
export class CalendarToolbarComponent {
  /**
   * The list of collaboratori.
   */
  readonly collaboratoriItems = input<Item[]>([]);

  /**
   * The collaboratore form group.
   */
  readonly collaboratoreFormGroup = input<FormGroup | null>(null);

  /**
   * The collaboratore control name.
   */
  readonly collaboratoreControlName = input<string>('collaboratore');

  /**
   * A flag to show/hide the collaboratore select.
   */
  readonly showCollaboratoreSelect = input<boolean>(true);

  /**
   * A flag to show/hide the collaboratore field.
   */
  readonly showCollaboratoreField = computed(
    () => this.showCollaboratoreSelect() && !!this.collaboratoreFormGroup()
  );

  /**
   * The label of the period.
   */
  readonly periodLabel = input<string>('');

  /**
   * View options.
   */
  readonly viewOptions = input<CalendarToolbarViewOption[]>([]);

  /**
   * The current view mode.
   */
  readonly viewMode = model<string>('');

  /**
   * The primary toggle value.
   */
  readonly secondaryViewOptions = input<CalendarToolbarViewOption[]>([]);

  /**
   * The current secondary view mode (e.g. the period, independent of the
   * primary `viewMode`'s mode/family).
   */
  readonly secondaryViewMode = model<string>('');

  /**
   * The date picker granularity.
   */
  readonly datePickerGranularity = input<'day' | 'month'>('day');

  /**
   * A flag to show/hide the add button.
   */
  readonly showAddButton = input<boolean>(false);

  /**
   * The add button options.
   */
  readonly addButtonOptions = input<string[]>([]);

  /**
   * When true, the add button is rendered visibly disabled (e.g. for a
   * read-only collaboratore) instead of hidden.
   */
  readonly addButtonDisabled = input<boolean>(false);

  /**
   * Emit the navigation action.
   */
  readonly navigate = output<'prev' | 'next' | 'today'>();

  /**
   * Emit the add new action.
   */
  readonly addNew = output<string>();

  /**
   * Emit the date selected.
   */
  readonly dateSelected = output<Date>();

  /**
   * On date change.
   * @param date
   */
  onDateChange(date: Date | null): void {
    if (date) this.dateSelected.emit(date);
  }

  /**
   * On month selected.
   * @param date
   * @param datepicker
   */
  onMonthSelected(date: Date, datepicker: MatDatepicker<Date>): void {
    if (this.datePickerGranularity() !== 'month') return;
    this.dateSelected.emit(date);
    datepicker.close();
  }
}
